export interface IntegrationReadiness {
  configuration: "valid" | "invalid" | "unknown";
  authenticationSetup: "ready" | "unavailable";
  ownConnection:
    | "missing"
    | "pending"
    | "active"
    | "expired"
    | "revoked"
    | "error";
  catalogue:
    | "not_applicable"
    | "never_fetched"
    | "current"
    | "partial"
    | "stale";
  catalogueFetchedAt: string | null;
  catalogueRefreshFailed: boolean;
  approvedToolCount: number | null;
  workspaceEnabled: boolean;
  canEnable: boolean;
  blockers: { code: string; safeMessage: string; action: string }[];
  observationToken: string;
}

export interface IntegrationDeletionImpact {
  deleted: boolean;
  enabled: boolean;
  connectionCount: number;
  affectedUserCount: number;
  dependencies: { id: string; label: string; kind: string }[];
  canDelete: boolean;
  cleanupStatus: string;
  observationToken: string;
}
import type { ApiClientConfig } from "./client-config.js";
import { apiCall } from "./http.js";
import type {
  IntegrationToolCatalogEntry,
  IntegrationCursorPage,
  IntegrationConnectionMode,
  IntegrationTemplate,
  RemoteRiskClass,
  RemoteToolLifecycle,
  TenantIntegration,
  ToolCatalogSyncResult,
} from "./integration.js";

/**
 * PB-1591 (EI-03) — the administrator console's backend client.
 *
 * The manifest types are IMPORTED FROM THE KERNEL, not restated here. That is
 * the whole reason this console can render a connector nobody has written yet:
 * `@playbookai/integration-kernel` owns `ConnectorManifestSchema`, the manifest
 * is validated against it before a template is ever seeded, and a second
 * structural definition on the frontend would be a copy to keep in sync and,
 * eventually, a place for provider knowledge to accumulate.
 *
 * These are `import type` only, so nothing from the kernel — least of all zod —
 * reaches the browser bundle.
 */
export type {
  ConnectorManifest,
  ManifestCapability,
  ManifestProduct,
} from "@playbookai/integration-kernel";

import type { ConnectorManifest } from "@playbookai/integration-kernel";

/** A catalog entry together with its manifest: the console's rendering input. */
export interface IntegrationTemplateDetail extends IntegrationTemplate {
  readonly manifest: ConnectorManifest;
  /**
   * Which connection modes this template can satisfy. Computed by the server:
   *  and the manifest’s  are different closed
   * vocabularies, and the rule bridging them has exactly one home.
   */
  readonly availableConnectionModes: readonly IntegrationConnectionMode[];
}

/**
 * The instance's editable configuration.
 *
 * `settings` and `accessPolicy` are open-ended by design: their permitted keys
 * come from the selected template's manifest, and the server validates against
 * it on write.
 */
export interface TenantIntegrationDetail extends TenantIntegration {
  readonly settings: Readonly<Record<string, unknown>>;
  readonly accessPolicy: IntegrationAccessPolicy;
  readonly createdBy: string;
  readonly updatedBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * PB-4542 — one user’s permission to use one integration instance.
 *
 * No enabled/revoked field: a grant exists or it does not, so the list IS the
 * complete answer and the console never has to filter it.
 */
export interface IntegrationGrant {
  userId: string;
  grantedBy: string;
  grantedAt: string;
}

/**
 * What a grant or revoke actually moved. `unchanged` is separate from `changed`
 * so the console can say “already had it” rather than reporting an idempotent
 * retry as if something happened.
 */
export interface IntegrationGrantChange {
  changed: string[];
  unchanged: string[];
}

/** Safe connection metadata visible to a tenant administrator. */
export interface AdminIntegrationConnection {
  readonly id: string;
  readonly subjectId: string | null;
  readonly displayName: string;
  readonly externalTenantId: string | null;
  readonly externalPrincipalName: string | null;
  readonly status: string;
  readonly transportMode: "rest" | "mcp";
  readonly mcpValidatedAt: string | null;
  readonly lastVerifiedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface IntegrationAccessPolicy {
  readonly products?: readonly string[];
  readonly capabilities?: readonly string[];
  readonly resources?: Readonly<Record<string, readonly string[]>>;
  /**
   * Per-capability, per-input-field allowlists of permitted values (PB-4734).
   * `resources` says WHERE an action may land; this says WHAT it may create
   * there. Absent means unrestricted; an empty array permits nothing.
   */
  readonly capabilityInputs?: Readonly<
    Record<string, Readonly<Record<string, readonly string[]>>>
  >;
  readonly rateLimit?: {
    readonly requestsPerMinute?: number;
    readonly burst?: number;
  };
}

export type IntegrationValidationStatus = "pass" | "warn" | "fail" | "skipped";

export interface IntegrationValidationCheck {
  readonly id: string;
  readonly label: string;
  readonly status: IntegrationValidationStatus;
  readonly message: string;
}

export interface IntegrationValidationResult {
  readonly integrationId: string;
  readonly ok: boolean;
  readonly checkedAt: string;
  readonly checks: readonly IntegrationValidationCheck[];
}

export interface VsmPromotionPolicy {
  readonly id: string;
  readonly sourceRefName: "vsm_kaizen";
  readonly transition: "promotion";
  readonly requiredIntent: "create_external_work";
  readonly tenantIntegrationId: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CreateTenantIntegrationInput {
  readonly templateId: string;
  readonly displayName: string;
  readonly description?: string;
  readonly connectionMode: IntegrationConnectionMode;
  readonly externalTenantId?: string;
  readonly settings?: Record<string, unknown>;
  readonly accessPolicy?: IntegrationAccessPolicy;
  readonly playerAiDefaultAccess?: "all_enabled" | "read_only";
}

export type UpdateTenantIntegrationInput = {
  expectedUpdatedAt?: string;
} & Partial<Omit<CreateTenantIntegrationInput, "templateId">>;

interface DataEnvelope<T> {
  readonly data: readonly T[];
}

/**
 * Built as a factory over `ApiClientConfig`, matching every other domain client
 * in this package — the shell injects the config, so nothing here reads auth
 * state or a base URL from a module-level singleton.
 */
export function createIntegrationAdminApi(config: ApiClientConfig) {
  return {
    listTemplates: (status?: string): Promise<IntegrationTemplate[]> =>
      apiCall<DataEnvelope<IntegrationTemplate>>(
        config,
        "GET",
        "/v1/integration-templates",
        undefined,
        status === undefined ? undefined : { status },
      ).then((r) => [...r.data]),

    getTemplate: (templateId: string): Promise<IntegrationTemplateDetail> =>
      apiCall<IntegrationTemplateDetail>(
        config,
        "GET",
        `/v1/integration-templates/${templateId}`,
      ),

    /**
     * PB-4542 — who may use this instance.
     *
     * Not a `DataEnvelope`: the contract returns a bare array here, because a
     * grant list is bounded by the tenant’s user count and never paged.
     */
    listGrants: (id: string): Promise<IntegrationGrant[]> =>
      apiCall<IntegrationGrant[]>(
        config,
        "GET",
        `/v1/tenant-integrations/${id}/grants`,
      ),

    listConnections: (id: string): Promise<AdminIntegrationConnection[]> =>
      apiCall<DataEnvelope<AdminIntegrationConnection>>(
        config,
        "GET",
        `/v1/tenant-integrations/${id}/connections`,
      ).then((response) => [...response.data]),

    grantAccess: (
      id: string,
      userIds: readonly string[],
    ): Promise<IntegrationGrantChange> =>
      apiCall<IntegrationGrantChange, { userIds: readonly string[] }>(
        config,
        "POST",
        `/v1/tenant-integrations/${id}/grants`,
        { userIds },
      ),

    /**
     * `…/revoke` rather than DELETE: the body carries the user list, which is
     * what makes the bulk case expressible.
     */
    revokeAccess: (
      id: string,
      userIds: readonly string[],
    ): Promise<IntegrationGrantChange> =>
      apiCall<IntegrationGrantChange, { userIds: readonly string[] }>(
        config,
        "POST",
        `/v1/tenant-integrations/${id}/grants/revoke`,
        { userIds },
      ),

    listIntegrations: (): Promise<TenantIntegration[]> =>
      apiCall<DataEnvelope<TenantIntegration>>(
        config,
        "GET",
        "/v1/tenant-integrations",
      ).then((r) => [...r.data]),

    getVsmPromotionPolicy: (): Promise<VsmPromotionPolicy | null> =>
      apiCall<VsmPromotionPolicy | undefined>(
        config,
        "GET",
        "/v1/admin/vsm/promotion-policy",
      ).then((policy) => policy ?? null),

    setVsmPromotionPolicy: (
      tenantIntegrationId: string,
    ): Promise<VsmPromotionPolicy> =>
      apiCall<VsmPromotionPolicy, { tenantIntegrationId: string }>(
        config,
        "PUT",
        "/v1/admin/vsm/promotion-policy",
        { tenantIntegrationId },
      ),

    removeVsmPromotionPolicy: (): Promise<void> =>
      apiCall(config, "DELETE", "/v1/admin/vsm/promotion-policy"),

    getIntegration: (id: string): Promise<TenantIntegrationDetail> =>
      apiCall<TenantIntegrationDetail>(
        config,
        "GET",
        `/v1/tenant-integrations/${id}`,
      ),

    createIntegration: (
      input: CreateTenantIntegrationInput,
    ): Promise<TenantIntegrationDetail> =>
      apiCall<TenantIntegrationDetail, CreateTenantIntegrationInput>(
        config,
        "POST",
        "/v1/tenant-integrations",
        input,
      ),

    updateIntegration: (
      id: string,
      input: UpdateTenantIntegrationInput,
    ): Promise<TenantIntegrationDetail> =>
      apiCall<TenantIntegrationDetail, UpdateTenantIntegrationInput>(
        config,
        "PATCH",
        `/v1/tenant-integrations/${id}`,
        input,
      ),

    readiness: (id: string): Promise<IntegrationReadiness> =>
      apiCall(config, "GET", `/v1/tenant-integrations/${id}/readiness`),
    deletionImpact: (id: string): Promise<IntegrationDeletionImpact> =>
      apiCall(config, "GET", `/v1/tenant-integrations/${id}/deletion-impact`),
    deleteIntegration: (
      id: string,
      expectedObservation: string,
    ): Promise<{ deleted: boolean; cleanupStatus: string }> =>
      apiCall(config, "POST", `/v1/tenant-integrations/${id}/retire`, {
        expectedObservation,
      }),
    retryCleanup: (id: string): Promise<IntegrationDeletionImpact> =>
      apiCall(config, "POST", `/v1/tenant-integrations/${id}/cleanup/retry`),

    setEnabled: (
      id: string,
      enabled: boolean,
      expectedObservation?: string,
    ): Promise<TenantIntegrationDetail> =>
      apiCall<
        TenantIntegrationDetail,
        { enabled: boolean; expectedObservation?: string }
      >(config, "PUT", `/v1/tenant-integrations/${id}/enabled`, {
        enabled,
        ...(expectedObservation ? { expectedObservation } : {}),
      }),

    validate: (id: string): Promise<IntegrationValidationResult> =>
      apiCall<IntegrationValidationResult>(
        config,
        "POST",
        `/v1/tenant-integrations/${id}/validate`,
        // A body-less POST. `undefined` keeps `Content-Type` off the request,
        // which is what the server expects for a no-payload action.
        undefined,
      ),

    listToolCatalog: (
      id: string,
      options: {
        readonly cursor?: string;
        readonly limit: 25 | 50;
        readonly query?: string;
        readonly lifecycle?: RemoteToolLifecycle;
        readonly riskClass?: RemoteRiskClass | "unclassified";
        readonly schemaUpdated?: boolean;
      } = { limit: 25 },
    ): Promise<
      IntegrationCursorPage<IntegrationToolCatalogEntry> & {
        readonly updatedEnabledCount?: number;
      }
    > =>
      apiCall<
        IntegrationCursorPage<IntegrationToolCatalogEntry> & {
          readonly updatedEnabledCount?: number;
        }
      >(config, "GET", `/v1/tenant-integrations/${id}/mcp/catalog`, undefined, {
        ...(options.cursor ? { cursor: options.cursor } : {}),
        limit: options.limit,
        ...(options.query ? { q: options.query } : {}),
        ...(options.lifecycle ? { lifecycle: options.lifecycle } : {}),
        ...(options.riskClass ? { riskClass: options.riskClass } : {}),
        ...(options.schemaUpdated ? { schemaUpdated: "true" } : {}),
      }).then((response) => {
        if (typeof response.hasMore === "boolean") return response;
        const needle = options.query?.toLocaleLowerCase();
        const rows = response.data.filter(
          (row) =>
            (!options.lifecycle || row.lifecycle === options.lifecycle) &&
            (!options.schemaUpdated || Boolean(row.schemaUpdatedAt)) &&
            (!options.riskClass ||
              (options.riskClass === "unclassified"
                ? row.riskClass === null
                : row.riskClass === options.riskClass)) &&
            (!needle ||
              `${row.toolName} ${row.title ?? ""} ${row.description ?? ""}`
                .toLocaleLowerCase()
                .includes(needle)),
        );
        const offset = options.cursor?.startsWith("legacy-")
          ? Number(options.cursor.slice("legacy-".length)) || 0
          : 0;
        const data = rows.slice(offset, offset + options.limit);
        const hasMore = offset + options.limit < rows.length;
        return {
          data,
          hasMore,
          updatedEnabledCount:
            response.updatedEnabledCount ??
            response.data.filter(
              (row) => row.lifecycle === "enabled" && row.schemaUpdatedAt,
            ).length,
          nextCursor: hasMore ? `legacy-${offset + options.limit}` : null,
          ...(offset === 0 ? { totalCount: rows.length } : {}),
        };
      }),

    getToolCatalogEntry: (
      id: string,
      entryId: string,
    ): Promise<IntegrationToolCatalogEntry> =>
      apiCall(
        config,
        "GET",
        `/v1/tenant-integrations/${id}/mcp/catalog/${entryId}`,
      ),

    syncToolCatalog: (
      id: string,
      connectionId: string,
    ): Promise<ToolCatalogSyncResult> =>
      apiCall<ToolCatalogSyncResult, { connectionId: string }>(
        config,
        "POST",
        `/v1/tenant-integrations/${id}/mcp/catalog/sync`,
        { connectionId },
      ),

    reviewToolCatalogEntry: (
      id: string,
      entryId: string,
      review: {
        lifecycle: Exclude<RemoteToolLifecycle, "withdrawn">;
        riskClass: RemoteRiskClass | null;
        /**
         * The `schemaHash` of the entry the administrator was LOOKING AT.
         *
         * Required, and it must come from the rendered entry rather than from a
         * fresh read: a catalogue sync can commit a new schema while the review
         * dialog is open, and this is what stops the approval attaching to a
         * shape nobody inspected. A mismatch answers
         * `INTEGRATION_CATALOG_REVIEW_STALE` (409) and changes nothing.
         */
        expectedSchemaHash: string;
      },
    ): Promise<IntegrationToolCatalogEntry> =>
      apiCall<IntegrationToolCatalogEntry, typeof review>(
        config,
        "PUT",
        `/v1/tenant-integrations/${id}/mcp/catalog/${entryId}`,
        review,
      ),
  };
}

export type IntegrationAdminApi = ReturnType<typeof createIntegrationAdminApi>;
